import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useLoader, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vNormalW;
  varying vec3 vPosW;
  void main() {
    vUv = uv;
    vNormalW = normalize(mat3(modelMatrix) * normal);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vPosW = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const fragmentShader = /* glsl */ `
  uniform sampler2D uDay;
  uniform sampler2D uNight;
  uniform sampler2D uDayH;
  uniform sampler2D uNightH;
  uniform sampler2D uWater;
  uniform sampler2D uBump;
  uniform vec3 uSunDir;
  uniform float uBumpScale;
  uniform float uHasWater;
  uniform float uHasBump;
  uniform float uMix;
  varying vec2 vUv;
  varying vec3 vNormalW;
  varying vec3 vPosW;

  vec3 perturbNormalArb(vec3 surf_pos, vec3 surf_norm, vec2 dHdxy) {
    vec3 vSigmaX = dFdx(surf_pos);
    vec3 vSigmaY = dFdy(surf_pos);
    vec3 vN = surf_norm;
    vec3 R1 = cross(vSigmaY, vN);
    vec3 R2 = cross(vN, vSigmaX);
    float fDet = dot(vSigmaX, R1);
    fDet *= (float(gl_FrontFacing) * 2.0 - 1.0);
    vec3 vGrad = sign(fDet) * (dHdxy.x * R1 + dHdxy.y * R2);
    return normalize(abs(fDet) * surf_norm - vGrad);
  }

  void main() {
    vec3 nGeo = normalize(vNormalW);
    vec3 sunDir = normalize(uSunDir);
    vec3 vDir = normalize(cameraPosition - vPosW);

    // Derivative-based terrain relief (applies once the bump map arrives)
    vec2 dHdxy = vec2(0.0);
    if (uHasBump > 0.5) {
      vec2 dSTdx = dFdx(vUv);
      vec2 dSTdy = dFdy(vUv);
      float Hll = uBumpScale * texture2D(uBump, vUv).x;
      float dBx = uBumpScale * texture2D(uBump, vUv + dSTdx).x - Hll;
      float dBy = uBumpScale * texture2D(uBump, vUv + dSTdy).x - Hll;
      dHdxy = vec2(dBx, dBy);
    }
    vec3 n = uHasBump > 0.5 ? perturbNormalArb(vPosW, nGeo, dHdxy) : nGeo;

    float sunD = dot(nGeo, sunDir);
    float sunDp = dot(n, sunDir);

    // Low-res preview crossfades into the 4K set as it streams in
    vec3 dayCol = mix(texture2D(uDay, vUv).rgb, texture2D(uDayH, vUv).rgb, uMix);
    vec3 nightCol = mix(texture2D(uNight, vUv).rgb, texture2D(uNightH, vUv).rgb, uMix);
    float water = 0.0;
    if (uHasWater > 0.5) water = texture2D(uWater, vUv).x;

    float diff = clamp(sunDp, 0.0, 1.0);
    vec3 day = dayCol * (0.14 + 1.12 * diff);

    // Sun glint on oceans only (geometric normal keeps it smooth)
    vec3 refl = reflect(-sunDir, nGeo);
    float spec = pow(max(dot(refl, vDir), 0.0), 32.0) * water;
    day += vec3(1.0, 0.94, 0.84) * spec * 0.3 * smoothstep(0.0, 0.28, sunD);

    float dayMix = smoothstep(-0.15, 0.3, sunD);
    vec3 night = nightCol * 1.25;
    vec3 color = mix(night, day, dayMix);

    // Soft warm terminator
    float term = exp(-pow(abs(sunD) * 4.5, 2.0));
    color += vec3(1.0, 0.5, 0.28) * term * 0.06;

    // Thin natural atmosphere rim — pale blue on the day side, violet whisper on the night limb
    float fres = pow(1.0 - clamp(dot(nGeo, vDir), 0.0, 1.0), 4.5);
    color += vec3(0.45, 0.62, 1.0) * fres * (0.2 + 0.3 * dayMix);
    color += vec3(0.44, 0.23, 1.0) * fres * (1.0 - dayMix) * 0.14;

    gl_FragColor = vec4(color, 1.0);
  }
`;

const atmosphereVertex = /* glsl */ `
  varying vec3 vNormalV;
  varying vec3 vNormalW;
  void main() {
    vNormalV = normalize(normalMatrix * normal);
    vNormalW = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const atmosphereFragment = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uSunDir;
  varying vec3 vNormalV;
  varying vec3 vNormalW;
  void main() {
    float z = clamp(-vNormalV.z, 0.0, 1.0);
    float falloff = pow(z, 1.7);
    vec3 sunDir = normalize(uSunDir);
    float dayBias = 0.55 + 0.45 * smoothstep(-0.4, 0.5, dot(normalize(vNormalW), sunDir));
    gl_FragColor = vec4(uColor * falloff * dayBias * 1.1, 1.0);
  }
`;

const SUN_DIR = new THREE.Vector3(5, 2, 4).normalize();
const PREVIEW = ['/textures/earth-day-1k.webp', '/textures/earth-night-1k.webp'];

export default function Earth({ mobile, reducedMotion }) {
  const globeRef = useRef();
  const cloudsRef = useRef();
  const gl = useThree((state) => state.gl);

  // Phase 1: tiny preview textures resolve almost instantly — no long suspend
  const [previewDay, previewNight] = useLoader(THREE.TextureLoader, PREVIEW);

  useMemo(() => {
    previewDay.colorSpace = THREE.SRGBColorSpace;
    previewNight.colorSpace = THREE.SRGBColorSpace;
  }, [previewDay, previewNight]);

  // Phase 2: the 4K set + water + bump stream in behind the preview
  const [hi, setHi] = useState(null);
  const [cloudsTex, setCloudsTex] = useState(null);

  useEffect(() => {
    let active = true;
    const loader = new THREE.TextureLoader();
    const aniso = Math.min(8, gl.capabilities.getMaxAnisotropy());
    Promise.all([
      loader.loadAsync('/textures/earth-day-4k.webp'),
      loader.loadAsync('/textures/earth-night-4k.webp'),
      loader.loadAsync('/textures/earth-water-2k.webp'),
      loader.loadAsync('/textures/earth-bump-2k.webp'),
    ])
      .then(([d, n, w, b]) => {
        if (!active) return;
        [d, n, w, b].forEach((t) => {
          t.anisotropy = aniso;
          t.generateMipmaps = true;
          t.minFilter = THREE.LinearMipmapLinearFilter;
        });
        d.colorSpace = THREE.SRGBColorSpace;
        n.colorSpace = THREE.SRGBColorSpace;
        setHi({ d, n, w, b });
      })
      .catch(() => {});
    loader
      .loadAsync('/textures/earth-clouds-2k.webp')
      .then((t) => {
        if (!active) return;
        t.anisotropy = aniso;
        t.colorSpace = THREE.SRGBColorSpace;
        setCloudsTex(t);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [gl]);

  const uniforms = useMemo(
    () => ({
      uDay: { value: previewDay },
      uNight: { value: previewNight },
      uDayH: { value: previewDay },
      uNightH: { value: previewNight },
      uSunDir: { value: SUN_DIR },
      uWater: { value: null },
      uBump: { value: null },
      uHasWater: { value: 0 },
      uHasBump: { value: 0 },
      uBumpScale: { value: 0 },
      uMix: { value: 0 },
    }),
    [previewDay, previewNight]
  );

  const mixRef = useRef(0);
  useEffect(() => {
    if (!hi) return;
    uniforms.uDayH.value = hi.d;
    uniforms.uNightH.value = hi.n;
    if (!mobile) {
      uniforms.uWater.value = hi.w;
      uniforms.uHasWater.value = 1;
      uniforms.uBump.value = hi.b;
      uniforms.uHasBump.value = 1;
      uniforms.uBumpScale.value = 0.07;
    }
  }, [hi, mobile, uniforms]);

  useFrame((state, delta) => {
    if (hi && mixRef.current < 1) {
      mixRef.current = Math.min(1, mixRef.current + delta * 0.8);
      uniforms.uMix.value = mixRef.current;
    }
    if (cloudsRef.current) {
      const mat = cloudsRef.current.material;
      if (mat.opacity < 0.85) mat.opacity = Math.min(0.85, mat.opacity + delta * 0.5);
    }
    if (globeRef.current && !reducedMotion) {
      globeRef.current.rotation.y += delta * 0.032;
    }
    if (cloudsRef.current && !reducedMotion) {
      cloudsRef.current.rotation.y += delta * 0.04;
    }
  });

  return (
    <group rotation={[0, 0, -0.41]}>
      <mesh ref={globeRef}>
        <sphereGeometry args={[2, mobile ? 72 : 108, mobile ? 72 : 108]} />
        <shaderMaterial
          vertexShader={vertexShader}
          fragmentShader={fragmentShader}
          uniforms={uniforms}
        />
      </mesh>

      {cloudsTex && (
        <mesh ref={cloudsRef}>
          <sphereGeometry args={[2.012, mobile ? 64 : 96, mobile ? 64 : 96]} />
          <meshLambertMaterial
            map={cloudsTex}
            transparent
            opacity={0}
            depthWrite={false}
          />
        </mesh>
      )}

      <mesh>
        <sphereGeometry args={[2.14, 64, 64]} />
        <shaderMaterial
          vertexShader={atmosphereVertex}
          fragmentShader={atmosphereFragment}
          uniforms={useMemo(
            () => ({ uColor: { value: new THREE.Color('#5b84e8') }, uSunDir: { value: SUN_DIR } }),
            []
          )}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          transparent
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}
